import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tabs,
  Tab,
  Button,
  Chip,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { monitoramentoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import SearchIcon from '@mui/icons-material/Search';
import GavelIcon from '@mui/icons-material/Gavel';
import VerifiedIcon from '@mui/icons-material/Verified';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import BlockIcon from '@mui/icons-material/Block';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import SchoolIcon from '@mui/icons-material/School';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';

const toISO = (d: Date): string => d.toISOString().slice(0, 10);

const CORES_PIE = ['#1E3A5F', '#2D7D9A', '#27AE60', '#F39C12', '#E74C3C', '#8E44AD', '#E67E22', '#16A085'];
const CORES_BARRAS: Record<string, string> = {
  ALVARA: '#27AE60',
  INSPECAO: '#1E3A5F',
  AUTO_INFRACAO: '#E74C3C',
  DENUNCIA: '#F39C12',
  NOTIFICACAO: '#8E44AD',
  INTERDICAO: '#C0392B',
};
const CORES_STATUS_DENUNCIA: Record<string, string> = {
  RECEBIDA: '#3498DB',
  EM_ANALISE: '#F39C12',
  RESOLVIDA: '#27AE60',
  DENUNCIA_PROCEDENTE: '#8E44AD',
};

function pivotTimeline(flat: any[]): { data: Record<string, any>[]; keys: string[] } {
  const keysSet = new Set<string>();
  const map = new Map<string, Record<string, any>>();
  for (const item of flat) {
    const semana = String(item.semana_inicio ?? '');
    const key = String(item.tipo_registro ?? item.status ?? '');
    keysSet.add(key);
    if (!map.has(semana)) map.set(semana, { semana_inicio: semana });
    map.get(semana)![key] = Number(item.total ?? 0);
  }
  return { data: Array.from(map.values()), keys: Array.from(keysSet) };
}

const SemDados: React.FC<{ msg?: string }> = ({ msg = 'Sem dados para o periodo selecionado.' }) => (
  <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
    {msg}
  </Typography>
);

const MonitoramentoVisa: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);

  const hoje = new Date();
  const tresMesesAtras = new Date();
  tresMesesAtras.setMonth(tresMesesAtras.getMonth() - 3);
  const [dataInicio, setDataInicio] = useState(toISO(tresMesesAtras));
  const [dataFim, setDataFim] = useState(toISO(hoje));

  useEffect(() => {
    if (!municipioAtivo?.id) {
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await monitoramentoService.getVisa(
          municipioAtivo.id,
          dataInicio,
          dataFim,
        );
        setData(result);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Erro ao carregar dados de VISA.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [municipioAtivo?.id, dataInicio, dataFim]);

  const kpis = data?.kpis || {};
  const porTipoRaw: any[] = useMemo(() => data?.porTipo || [], [data]);
  const timelineRaw: any[] = data?.timeline || [];
  const porRiscoRaw: any[] = data?.porRisco || [];

  const controleAdmin = data?.controle_administrativo || { kpis: {}, mensal: [] };
  const denuncias = data?.denuncias || { por_subtipo: [], taxa: {}, timeline: [] };
  const cadastroMensal: any[] = data?.cadastro_mensal || [];
  const acoesEdu = data?.acoes_educativas || { kpis: {}, por_tipo: [], mensal: [], recentes: [] };
  const rmnra = data?.rmnra || { kpis: {}, por_mes: [], estabelecimentos: [] };

  const porTipoChart = useMemo(
    () => porTipoRaw.map((item: any) => ({
      name: item.tipo_label || item.tipo_registro,
      value: Number(item.total ?? 0),
    })),
    [porTipoRaw],
  );

  const { data: timelineData, keys: timelineTipos } = useMemo(
    () => pivotTimeline(timelineRaw),
    [timelineRaw],
  );

  const porRiscoChart = useMemo(
    () => porRiscoRaw.map((item: any) => ({
      name: item.nivel_risco || item.risco || 'N/I',
      value: Number(item.total ?? 0),
    })),
    [porRiscoRaw],
  );

  const denSubtipoChart = useMemo(
    () => (denuncias.por_subtipo || []).map((r: any) => ({
      name: r.subtipo,
      value: Number(r.total ?? 0),
    })),
    [denuncias.por_subtipo],
  );

  const denStatusChart = useMemo(() => {
    const t = denuncias.taxa || {};
    return [
      { name: 'Recebidas', value: Number(t.recebidas ?? 0) },
      { name: 'Em analise', value: Number(t.em_analise ?? 0) },
      { name: 'Resolvidas', value: Number(t.resolvidas ?? 0) },
      { name: 'Procedentes', value: Number(t.procedentes ?? 0) },
    ].filter((x) => x.value > 0);
  }, [denuncias.taxa]);

  const { data: denTimelineData, keys: denTimelineKeys } = useMemo(
    () => pivotTimeline(denuncias.timeline || []),
    [denuncias.timeline],
  );

  const eduPorTipoChart = useMemo(
    () => (acoesEdu.por_tipo || []).map((r: any) => ({
      name: r.tipo,
      total: Number(r.total ?? 0),
      participantes: Number(r.participantes ?? 0),
    })),
    [acoesEdu.por_tipo],
  );

  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', px: 3 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Nenhum municipio selecionado
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Selecione um municipio para visualizar dados de Vigilancia Sanitaria.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Skeleton variant="text" width="30%" height={48} />
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mt: 2, mb: 3 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  const renderVisaoGeral = () => (
    <>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard titulo="Inspecoes" valor={Number(kpis.total_inspecoes ?? 0).toLocaleString('pt-BR')} icone={<SearchIcon />} cor="#1E3A5F" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard titulo="Autos de Infracao" valor={Number(kpis.total_autos_infracao ?? 0).toLocaleString('pt-BR')} icone={<GavelIcon />} cor="#E74C3C" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard titulo="Alvaras Emitidos" valor={Number(kpis.total_alvaras ?? 0).toLocaleString('pt-BR')} icone={<VerifiedIcon />} cor="#27AE60" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard titulo="Denuncias" valor={Number(kpis.total_denuncias ?? 0).toLocaleString('pt-BR')} icone={<ReportProblemIcon />} cor="#F39C12" />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Acoes por Tipo</Typography>
            {porTipoChart.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={porTipoChart} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100}
                    label={({ name, percent }: any) => `${name}: ${(percent * 100).toFixed(0)}%`} labelLine={{ strokeWidth: 1 }}>
                    {porTipoChart.map((_: any, index: number) => (
                      <Cell key={index} fill={CORES_PIE[index % CORES_PIE.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: any) => [Number(value).toLocaleString('pt-BR'), 'Total']} />
                </PieChart>
              </ResponsiveContainer>
            ) : <SemDados msg="Sem dados por tipo de acao." />}
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Evolucao Semanal</Typography>
            {timelineData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={timelineData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="semana_inicio" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v: number) => v.toLocaleString('pt-BR')} />
                  <Tooltip formatter={(value: any, name: any) => [Number(value).toLocaleString('pt-BR'), name]} />
                  <Legend />
                  {timelineTipos.map((tipo, idx) => (
                    <Bar key={tipo} dataKey={tipo} name={tipo} fill={CORES_BARRAS[tipo] || CORES_PIE[idx % CORES_PIE.length]} radius={[4, 4, 0, 0]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : <SemDados msg="Sem dados de evolucao semanal." />}
          </Paper>
        </Grid>
      </Grid>

      {porRiscoChart.length > 0 && (
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Distribuicao por Nivel de Risco</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={porRiscoChart} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100}
                label={({ name, percent }: any) => `${name}: ${(percent * 100).toFixed(0)}%`} labelLine={{ strokeWidth: 1 }}>
                {porRiscoChart.map((_: any, index: number) => (
                  <Cell key={index} fill={CORES_PIE[index % CORES_PIE.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value: any) => [Number(value).toLocaleString('pt-BR'), 'Total']} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Paper>
      )}

      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Resumo por Tipo de Registro</Typography>
        {porTipoRaw.length > 0 ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>%</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const totalGeral = porTipoRaw.reduce((acc: number, r: any) => acc + Number(r.total ?? 0), 0);
                  return porTipoRaw.map((row: any, index: number) => {
                    const total = Number(row.total ?? 0);
                    const pct = totalGeral > 0 ? ((total / totalGeral) * 100).toFixed(1) : '0.0';
                    return (
                      <TableRow key={index}>
                        <TableCell>{row.tipo_label || row.tipo_registro}</TableCell>
                        <TableCell align="right">{total.toLocaleString('pt-BR')}</TableCell>
                        <TableCell align="right">{pct}%</TableCell>
                      </TableRow>
                    );
                  });
                })()}
              </TableBody>
            </Table>
          </Box>
        ) : <SemDados />}
      </Paper>

      {data && (
        <AnalisePredicaoCard dadosContexto={JSON.stringify(data)} municipioId={municipioAtivo.id} painelId="visa" contexto="dashboard" />
      )}
    </>
  );

  const renderControleAdmin = () => {
    const caKpis = controleAdmin.kpis || {};
    const mensal = controleAdmin.mensal || [];
    return (
      <>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <KpiCard titulo="Autos de Infracao" valor={Number(caKpis.total_autos ?? 0).toLocaleString('pt-BR')} icone={<GavelIcon />} cor="#E74C3C" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <KpiCard titulo="Interdicoes" valor={Number(caKpis.total_interdicoes ?? 0).toLocaleString('pt-BR')} icone={<BlockIcon />} cor="#C0392B" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <KpiCard titulo="Processos Ativos" valor={Number(caKpis.processos_ativos ?? 0).toLocaleString('pt-BR')} icone={<FolderOpenIcon />} cor="#8E44AD" />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Autos e Interdicoes por Mes</Typography>
              {mensal.length > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={mensal}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v: number) => v.toLocaleString('pt-BR')} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="autos" name="Autos" fill="#E74C3C" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="interdicoes" name="Interdicoes" fill="#C0392B" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <SemDados />}
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Cadastro de Estabelecimentos</Typography>
              {cadastroMensal.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={cadastroMensal}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="novos_cadastros" name="Novos" stroke="#2D7D9A" strokeWidth={2} dot={{ r: 3 }} />
                    <Line yAxisId="right" type="monotone" dataKey="total_acumulado" name="Acumulado" stroke="#1E3A5F" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <SemDados />}
              <Button variant="outlined" size="small" startIcon={<LocationOnIcon />} sx={{ mt: 2 }}
                onClick={() => navigate('/monitoramento/visa-geo')}>
                Ver no mapa
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </>
    );
  };

  const renderDenuncias = () => {
    const taxa = denuncias.taxa || {};
    return (
      <>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard titulo="Taxa de Atendimento" valor={`${Number(taxa.taxa_percentual ?? 0).toLocaleString('pt-BR')}%`}
              icone={<TrendingUpIcon />} cor="#27AE60" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard titulo="Recebidas" valor={Number(taxa.recebidas ?? 0).toLocaleString('pt-BR')} icone={<ReportProblemIcon />} cor="#3498DB" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard titulo="Em Analise" valor={Number(taxa.em_analise ?? 0).toLocaleString('pt-BR')} icone={<SearchIcon />} cor="#F39C12" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard titulo="Resolvidas + Procedentes" valor={(Number(taxa.resolvidas ?? 0) + Number(taxa.procedentes ?? 0)).toLocaleString('pt-BR')}
              icone={<VerifiedIcon />} cor="#8E44AD" />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={5}>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Denuncias por Subtipo</Typography>
              {denSubtipoChart.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={denSubtipoChart} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100}
                      label={({ name, percent }: any) => `${name}: ${(percent * 100).toFixed(0)}%`}>
                      {denSubtipoChart.map((_: any, i: number) => (
                        <Cell key={i} fill={CORES_PIE[i % CORES_PIE.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : <SemDados />}
            </Paper>
          </Grid>
          <Grid item xs={12} md={7}>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Denuncias por Status</Typography>
              {denStatusChart.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={denStatusChart} layout="vertical" margin={{ left: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(v: number) => v.toLocaleString('pt-BR')} />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="value" name="Total" fill="#F39C12" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <SemDados />}
            </Paper>
          </Grid>
        </Grid>

        <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Evolucao Semanal das Denuncias</Typography>
          {denTimelineData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={denTimelineData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="semana_inicio" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v: number) => v.toLocaleString('pt-BR')} />
                <Tooltip />
                <Legend />
                {denTimelineKeys.map((st, idx) => (
                  <Bar key={st} dataKey={st} name={st} fill={CORES_STATUS_DENUNCIA[st] || CORES_PIE[idx % CORES_PIE.length]} stackId="a" />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : <SemDados />}
        </Paper>
      </>
    );
  };

  const renderEducativasRmnra = () => {
    const eduKpis = acoesEdu.kpis || {};
    const rmnraKpis = rmnra.kpis || {};
    return (
      <>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Acoes Educativas</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <KpiCard titulo="Total de Acoes" valor={Number(eduKpis.total_acoes ?? 0).toLocaleString('pt-BR')} icone={<SchoolIcon />} cor="#2D7D9A" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <KpiCard titulo="Participantes" valor={Number(eduKpis.total_participantes ?? 0).toLocaleString('pt-BR')} icone={<PeopleIcon />} cor="#1E3A5F" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <KpiCard titulo="Setores Atendidos" valor={Number(eduKpis.setores_distintos ?? 0).toLocaleString('pt-BR')} icone={<VerifiedIcon />} cor="#27AE60" />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Acoes por Tipo</Typography>
              {eduPorTipoChart.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={eduPorTipoChart}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tickFormatter={(v: number) => v.toLocaleString('pt-BR')} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="total" name="Acoes" fill="#2D7D9A" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="participantes" name="Participantes" fill="#1E3A5F" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <SemDados />}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Ultimas Acoes Realizadas</Typography>
              {(acoesEdu.recentes || []).length > 0 ? (
                <Box sx={{ overflowX: 'auto', maxHeight: 280 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Data</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Tema</TableCell>
                        <TableCell align="right">Part.</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(acoesEdu.recentes || []).map((r: any) => (
                        <TableRow key={r.id}>
                          <TableCell>{r.data_fmt}</TableCell>
                          <TableCell><Chip label={r.tipo} size="small" /></TableCell>
                          <TableCell sx={{ maxWidth: 180 }}>{r.tema}</TableCell>
                          <TableCell align="right">{r.participantes}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ) : <SemDados />}
            </Paper>
          </Grid>
        </Grid>

        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Controle RMNRA — Comercio de Medicamentos</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard titulo="Envio no Prazo" valor={`${Number(rmnraKpis.taxa_envio_prazo ?? 0).toLocaleString('pt-BR')}%`}
              icone={<LocalPharmacyIcon />} cor="#27AE60" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard titulo="Farmacias Monitoradas" valor={Number(rmnraKpis.total_farmacias ?? 0).toLocaleString('pt-BR')}
              icone={<LocalPharmacyIcon />} cor="#1E3A5F" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard titulo="Pendentes" valor={Number(rmnraKpis.pendentes ?? 0).toLocaleString('pt-BR')} icone={<ReportProblemIcon />} cor="#F39C12" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard titulo="Atrasados" valor={Number(rmnraKpis.atrasados ?? 0).toLocaleString('pt-BR')} icone={<BlockIcon />} cor="#E74C3C" />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>RMNRA por Mes</Typography>
              {(rmnra.por_mes || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={rmnra.por_mes}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
                    <YAxis tickFormatter={(v: number) => v.toLocaleString('pt-BR')} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="enviados" name="Enviados" fill="#27AE60" stackId="s" />
                    <Bar dataKey="pendentes" name="Pendentes" fill="#F39C12" stackId="s" />
                    <Bar dataKey="atrasados" name="Atrasados" fill="#E74C3C" stackId="s" />
                  </BarChart>
                </ResponsiveContainer>
              ) : <SemDados />}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Farmacias com Pendencia ou Atraso</Typography>
              {(rmnra.estabelecimentos || []).length > 0 ? (
                <Box sx={{ overflowX: 'auto', maxHeight: 280 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Estabelecimento</TableCell>
                        <TableCell>Mes</TableCell>
                        <TableCell align="right">Notif. A</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(rmnra.estabelecimentos || []).map((r: any, i: number) => (
                        <TableRow key={`${r.cnpj}-${r.mes}-${i}`}>
                          <TableCell>{r.nome_fantasia}</TableCell>
                          <TableCell>{r.mes}</TableCell>
                          <TableCell align="right">{r.notificacoes_receita_a}</TableCell>
                          <TableCell>
                            <Chip label={r.status_envio} size="small"
                              color={r.status_envio === 'ATRASADO' ? 'error' : r.status_envio === 'PENDENTE' ? 'warning' : 'success'} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ) : <SemDados msg="Nenhuma farmacia com pendencia no periodo." />}
            </Paper>
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Vigilancia Sanitaria (VISA)</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>{municipioAtivo.nome}</Typography>
        </Box>
        <DateRangeSelector dataInicio={dataInicio} dataFim={dataFim} onChange={(di, df) => { setDataInicio(di); setDataFim(df); }} />
      </Box>

      <Paper elevation={0} sx={{ mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Visao Geral" />
          <Tab label="Controle Administrativo" />
          <Tab label="Gestao de Denuncias" />
          <Tab label="Educativas e RMNRA" />
        </Tabs>
      </Paper>

      {tab === 0 && renderVisaoGeral()}
      {tab === 1 && renderControleAdmin()}
      {tab === 2 && renderDenuncias()}
      {tab === 3 && renderEducativasRmnra()}

      <ChatIAButton contexto="dashboard" dadosContexto={JSON.stringify(data)} fabSx={{ bottom: 24, right: 24, top: 'auto' }} />
    </Box>
  );
};

export default MonitoramentoVisa;
